import { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from "react";
import { SpeakerIcon, StopIcon } from "./Icons";
import ui from "./ui.module.css";

// Approved curriculum sound: the prompt read aloud, or an item's name. It is
// teaching content, never the learner's work, so playing it saves nothing.
//
// One player per activity, so only one clip is ever heard at a time, and it
// starts only when the learner taps. A clip is fetched with the tablet's own
// credentials the first time it is played and kept for the activity.

const PlayerContext = createContext(null);

export const AudioProvider = ({ children }) => {
  const audio = useRef(null);
  const cache = useRef(new Map());
  // The button that started the clip, so one clip used in two places only
  // shows as playing where it was tapped.
  const [current, setCurrent] = useState(null); // { owner, state: "loading" | "playing" }
  const [failed, setFailed] = useState(() => new Set());

  const stop = useCallback(() => {
    audio.current?.pause();
    setCurrent(null);
  }, []);

  useEffect(() => {
    const clips = cache.current;
    return () => {
      audio.current?.pause();
      clips.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const fail = useCallback((src) => {
    setCurrent(null);
    setFailed((set) => new Set(set).add(src));
  }, []);

  const play = useCallback(
    async (src, owner) => {
      audio.current?.pause();
      setCurrent({ owner, state: "loading" });
      try {
        let url = cache.current.get(src);
        if (!url) {
          const response = await fetch(src, { credentials: "include" });
          if (!response.ok) throw new Error(`audio ${response.status}`);
          url = URL.createObjectURL(await response.blob());
          cache.current.set(src, url);
        }
        if (!audio.current) audio.current = new Audio();
        const player = audio.current;
        player.onended = () => setCurrent(null);
        player.onerror = () => fail(src);
        player.src = url;
        player.currentTime = 0;
        await player.play();
        // Another tap may have started a different clip meanwhile.
        setCurrent((c) => (c?.owner === owner ? { owner, state: "playing" } : c));
      } catch {
        fail(src);
      }
    },
    [fail]
  );

  return (
    <PlayerContext.Provider value={{ current, failed, play, stop }}>
      {children}
    </PlayerContext.Provider>
  );
};

// A large Listen button. `audio` is a resolved asset or null; with no clip,
// or one that could not be played, there is simply no button and the
// picture and words carry on as they are.
export const Listen = ({ audio, label, small = false }) => {
  const player = useContext(PlayerContext);
  const owner = useId();
  if (!audio || !player || player.failed.has(audio.src)) return null;
  const mine = player.current?.owner === owner ? player.current.state : null;
  const name = audio.alt || label || "this";
  return (
    <button
      type="button"
      className={[ui.listen, small && ui.listenSmall, mine && ui.listenOn]
        .filter(Boolean)
        .join(" ")}
      onClick={(event) => {
        event.stopPropagation();
        if (mine) player.stop();
        else player.play(audio.src, owner);
      }}
      aria-label={mine ? `Stop: ${name}` : `Listen: ${name}`}
      aria-pressed={Boolean(mine)}
    >
      {mine ? <StopIcon size={small ? 28 : 36} /> : <SpeakerIcon size={small ? 28 : 36} />}
      {!small && <span>{mine === "loading" ? "…" : mine ? "Stop" : "Listen"}</span>}
    </button>
  );
};

// The step's instruction with its Listen button beside it.
export const Prompt = ({ step }) =>
  step.prompt || step.promptAudio ? (
    <div className={ui.promptRow}>
      {step.prompt && <h1 className={ui.prompt}>{step.prompt}</h1>}
      <Listen audio={step.promptAudio} label={step.prompt} />
    </div>
  ) : null;

export const useStopAudio = () => useContext(PlayerContext)?.stop;
